import React, { JSX, useMemo } from 'react';

import { useFormikContext } from 'formik';

import { Box, FormControl, FormHelperText, FormLabel, TextField } from '@mui/material';
import { TextFieldProps } from '@mui/material/TextField';
import { store } from '@store/index';

import { textErrors } from '../../../helpers';
import { editableCondition } from '../../../hooks/useRenderCondition';
import { useT } from '../../../hooks/useT';
import { TextFieldControlModelInterface } from '../../../interfaces';
import { OOFlowWrapper } from '../../../wrappers/FlowWrapper';
import { HtmlCustom } from '../../fields/HtmlCustom';
import { FormControlLoader } from '../../FormControlLoader';

import styles from './TextField.module.scss';

type OOMaskedTextFieldProps = {
  controlModel: TextFieldControlModelInterface;
  textFieldProps?: Omit<TextFieldProps, 'value'>;
  onChangeCallback?: (event: any) => void;
  hasHtmlLabel?: boolean;
};

export const OOMaskedTextField: React.FC<OOMaskedTextFieldProps> = ({
  controlModel,
  textFieldProps,
  onChangeCallback,
  hasHtmlLabel,
}: OOMaskedTextFieldProps) => {
  const { t, translationsLoading } = useT('entry', 'approval', 'documents', 'hiring');
  const formik = useFormikContext<Record<string, any>>();
  const fieldProps = formik.getFieldProps(controlModel.name);
  const fieldMeta = formik.getFieldMeta(controlModel.name);
  const flowData = store.getState().oneOnboarding.flow;
  const isRecruiterLogin = OOFlowWrapper.isRecruiter();
  const textValue = controlModel?.value ?? '';

  let prefix = controlModel.label.split('.')[0].toLowerCase();
  const prefixAlias: any = {
    documents2: 'documents',
    documents3: 'documents',
  };
  if (prefixAlias[prefix]) {
    prefix = prefixAlias[prefix];
  }

  const flowWrapper = useMemo(() => {
    if (!flowData) return null;
    return OOFlowWrapper.create(flowData);
  }, [flowData]);

  const isDisabled = !(
    flowWrapper?.isControlEditable(controlModel.isEditableCandidate, controlModel.isEditableRecruiter) ?? true
  );

  const isEditable = editableCondition(controlModel, flowWrapper, formik.values) ?? true;

  if (controlModel?.defaultValue && !fieldProps?.value.trim()) {
    formik.setFieldValue(controlModel.name, controlModel.defaultValue);
  }

  const onChange = (event: any) => {
    formik.handleChange(event);
    if (onChangeCallback) {
      onChangeCallback(event);
    }
  };
  if (translationsLoading) {
    return <FormControlLoader />;
  }

  const getMaskedTextElements = () => {
    const maskedElements: JSX.Element[] = [];
    const textChars = textValue.split('');
    const initialChars = textChars.splice(0, 2);
    const lastChars = textChars.length > 4 ? textChars.slice(textChars.length - 4) : [];
    const maskedChars = textChars.length > 4 ? textChars.slice(0, textChars.length - 4) : textChars;

    initialChars.forEach((ch: string, i: number) => {
      maskedElements.push(<div key={`init-${i + 1}`}>{ch}</div>);
    });
    maskedChars.forEach((ch: string, i: number) => {
      maskedElements.push(
        <div key={`masked-${i + 1}`} className={styles.textFieldContainer__maskedTextContainer__maskedDots} />,
      );
    });
    lastChars.forEach((ch: string, i: number) => {
      maskedElements.push(<div key={`end-${i + 1}`}>{ch}</div>);
    });
    return maskedElements;
  };

  return (
    <FormControl className={styles.textFieldContainer}>
      {t(`${prefix}:${controlModel.label}`) && (
        <Box pb={1} sx={{ paddingBottom: '0; !important' }}>
          <FormLabel>
            {!hasHtmlLabel ? (
              `${t(`${prefix}:${controlModel.label}`)}${controlModel.isMandatory ? '*' : ''}`
            ) : (
              <HtmlCustom rawHtml={`${t(`${prefix}:${controlModel.label}`)}${controlModel.isMandatory ? '*' : ''}`} />
            )}
          </FormLabel>
        </Box>
      )}
      {textValue &&
      ((isRecruiterLogin && !controlModel.isEditableRecruiter) ||
        (!isRecruiterLogin && !controlModel.isEditableCandidate)) ? (
        <>
          <Box className={styles.textFieldContainer__maskedTextContainer}>{getMaskedTextElements()}</Box>
          {fieldMeta.error && <FormHelperText error>{t(`${fieldMeta.error}`)}</FormHelperText>}
        </>
      ) : (
        <TextField
          {...fieldProps}
          {...textErrors(fieldMeta)}
          {...textFieldProps}
          InputLabelProps={{ required: controlModel.isMandatory }}
          fullWidth={true}
          placeholder={t(`${prefix}:${controlModel.staticText || controlModel.label}`)}
          onChange={onChange}
          disabled={isDisabled || !isEditable}
          defaultValue={controlModel.defaultValue}
        />
      )}

      {controlModel.description && <FormHelperText>{t(`${prefix}:${controlModel.description}`)}</FormHelperText>}
    </FormControl>
  );
};
